import {
  OwnerInfo,
  SeverityEnum,
  SeverityFieldNames,
} from './general.interface';
import { StatusIconType } from './procedures.interface';
import { Comment } from './comment.interface';

export interface Risk {
  riskId: string;
  riskTemplateId: string;
  canEditManual: boolean;
  canCompareStrategies: boolean;
  riskDescription: string;
  wasAddedManually: boolean;
  status: string;
  statusType?: RiskStatusTypeName;
  subStatus?: string;
  severity: SeverityEnum;
  riskName: string;
  impactCost: number;
  mitigationStrategies: RiskMitigationStrategy[];
  complianceViolations: ComplianceViolation;
  statusComment?: string;
  isFullyMapped: boolean;
  fromActivePlan: boolean;
  /** True when the risk is on an Approved or In Progress plan (any status). Feature 1881. */
  isOnCommittedPlan?: boolean;
  targetDate?: string;
  totalControlsCount: number;
  selectedControlsCount: number;
}

export interface ActiveRisk extends Risk {
  isReviewed: boolean;
  unhandledRisksCount: number;
  ownerInfo?: OwnerInfo;
  defaultStakeholders: number;
  riskCategory?: RiskCategory;
}

export enum RiskCategory {
  NEW = 'New',
  MITIGATION_ACTIVE = 'ActiveMitigation',
  UNHANDLED = 'Unhandled',
  MITIGATION_PENDING = 'PendingMitigation',
}

export interface WarningBox {
  mode: 'Alert' | 'Error';
  visible: boolean;
  fullBox?: { messageId: string };
  minimizedBox?: { messageId: string };
  counts: {
    isSingularNew: boolean;
    isSingularTotal: boolean;
    newRisksCount: number;
    totalUnmitigatedCount: number;
    unhandledRisksCount: number;
  };
}

export interface RiskLevel {
  options: RiskLevelItem[];
  max?: RiskLevelItem;
  min?: RiskLevelItem;
}

export interface RiskLevelItem {
  id: string;
  value: number;
  label: string;
}

export interface RiskDetails {
  riskInOrgId: string;
  planId: string;
  instigators: string;
  financialImpact: number;
  likelihoodLevelId: number;
  impactLevelId: number;
  priority: number;
  riskProbability: number;
  riskTemplateId: string;
  riskTemplateName: string;
  rowVersion: string;
  controlMappings?: RiskControlMapping[];
  actionMappings: RiskActionMapping[];
  wasAddedManually?: boolean;
}

export interface RiskActionMapping {
  actionId: string;
  actionName: string;
  controlTemplateIds: string[];
  assetTypeSolutions: AssetTypeSolutionMapping[];
  controlCount: number;
  totalCost: number;
  isUnified: boolean;
}

export interface AssetTypeSolutionMapping {
  assetTypeId: string;
  assetName: string;
  isServiceManual: boolean;
  isToolManual: boolean;
  selectedServiceName?: string;
  selectedServiceId?: string;
  selectedToolName?: string;
  selectedToolId?: string;
  selectedLicenseTypeId?: string;
  selectedVendorName?: string;
  isCarrier?: boolean;
  isOnlyFromThisRisk?: boolean;
}

export interface RiskControlMapping {
  controlTemplateId: string;
  controlTemplateName: string;
  assetTypeIds: string[];
  assetTypesWithNames: { assetTypeId: string; assetTypeName: string }[];
}

export interface RiskActionDTO {
  planId: string;
  controlAssetSelection?: {
    controlId: string;
    assetTypeIds: string[];
  }[];
}

export interface InactiveRisk {
  id: string;
  name: string;
}

export interface ControlActionMappingDTO {
  controls: ControlActionMapping[];
  planEndDate: string;
}

export interface ControlActionMapping {
  controlId: string;
  controlName: string;
  isPrerequisite: boolean;
  assetTypeIds: string[];
  actions: ActionSolutionMapping[];
}

export interface ActionSolutionMapping {
  actionDescription: string;
  actionId: string;
  actionName: string;
  allowedAssetTypeIds: string[];
  assetTypeSelections: RiskActionExistingPlanMapping[];
  controlsInPlanCount: number;
  existingPlanMappings: RiskActionExistingPlanMapping[];
  isUnified: boolean;
  requiresUpdate: boolean;
  outOfScopeAssetTypeIds: string[];
  solutionsCatalog: {
    commercial: RiskSolutionCommercial;
    solutionId: string;
    solutionName: string;
    solutionType: number;
    vendorName: string;
  }[];
}

export interface RiskControl {
  id: string;
  name: string;
  assetTypes: AssetType[];
  actions: RiskAction[];
}

export interface RiskActionExistingPlanMapping {
  assetTypeId: string;
  requiredByControlIds: string[];
  serviceSolutionId: string;
  toolSolutionId: string;
}

export interface RiskAction {
  id: string;
  controlCount: number;
  name: string;
  controlId: string;
  controlName: string;
  remediationSolution: RiskActionRemediationSolution;
  assetTypes: RiskActionAssetType[];
  toolType: RiskActionToolType;
  toolOptions: RiskActionToolService[];
  serviceOptions: RiskActionToolService[];
  tool: RiskActionToolService;
  service: RiskActionToolService;
  allowedAssetTypeIds: string[];
  outOfScopeAssetTypeIds: string[];
  requiresUpdate: boolean;
  controlTemplateIds: string[];
  existingPlanMappings: RiskActionExistingPlanMapping[];
}

export interface RiskActionsMapped extends RiskAction {
  controlTemplateIds: string[];
  controlCount: number;
}

export interface RiskControlOptions {
  controls: { id: string; name: string; isPrerequisite: boolean }[];
  prerequisiteMap: { [key: string]: string[] };
}

export interface RiskSolutionCommercial {
  costAmount: number;
  customerSolutionId: string;
  cheapestLicenseTypeId: string;
  cheapestLicenseTypeName: string;
  hasCustomerRecord: boolean;
  isActive: boolean;
  isInPlan: boolean;
  isAcquired: boolean;
  isLicenseExpired: boolean;
  licenseEndUtc: string;
  licenseStartUtc: string;
  planEndDate: string;
  solutionAddedManually: boolean;
}

export interface RiskActionToolService {
  solutionId: string;
  solutionName: string;
  solutionType: RiskActionToolType;
  isCustomerDefined: boolean;
  vendorName: string;
  commercial: RiskSolutionCommercial;
}

export interface RiskActionAssetType {
  id: string;
  name: string;
  toolType: RiskActionToolType;
  tool: RiskActionToolService;
}

export enum RiskActionRemediationSolution {
  UNIFIED = 1,
  MULTIPLE = 2,
}

export enum RiskActionToolType {
  TOOL = 1,
  SERVICE = 2,
}

export interface ActionOption {
  id: string;
  name: string;
  subCategories?: RiskControlSubCategory[];
}

export interface ControlAssetType {
  [key: string]: AssetType[];
}

export interface AssetType {
  assetTypeId: string;
  assetTypeInfo: {
    assetTypeName: string;
    assetTypeDescription: string;
    assetMainTypeName: string;
    assetsCount: number;
  };
}

export interface RiskControlSubCategory {
  id: string;
  name: string;
}

export interface ComplianceViolation {
  contractual: boolean;
  regulatory: boolean;
  sensitiveData: boolean;
}

export interface RiskMitigationStrategy {
  type: RiskMitigationStrategyType;
  id?: string;
  strategyType: string;
  isSelected: boolean;
  estimatedCost?: number;
  targetDate: string;
  options: RiskMitigationStrategyOption[];
}

export interface RiskMitigationStrategyOption {
  name: string;
  isSelected: boolean;
}

export enum RiskMitigationStrategyType {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,
}

export interface RiskCoverage {
  breakdown: {
    [SeverityFieldNames.LOW]: RiskCoverageBreakdown;
    [SeverityFieldNames.MEDIUM]: RiskCoverageBreakdown;
    [SeverityFieldNames.HIGH]: RiskCoverageBreakdown;
    [SeverityFieldNames.CRITICAL]: RiskCoverageBreakdown;
  };
  completedInProgram: number;
  coveredRisks: number;
  existingCovered: number;
  newCovered: number;
  percentageCovered: number;
  totalRisks: number;
  uncompletedRisks: number;
}

export interface RiskCoverageBreakdown {
  totalRisks: number;
  coveredRisks: number;
  percentageCovered: number;
  newCovered: number;
  completedInProgram: number;
}

export interface RiskContractualComplianceCoverage {
  covered: number; //controls this plan addresses (existing + new)
  existingCovered: number; //this plan's controls already covered (Active+Approved)
  newCovered: number; //this plan's new controls
  uncompleted: number; //totalControls − covered
  completedInProgram: number; //program-wide covered controls
  remainingControls: number; //required controls neither covered nor in this plan
  percentageCompleted: number; //completed %
  totalControls: number; //required controls relevant to org
}

export interface RiskLocalLawsComplianceCoverage {
  coveredControls: number;
  percentageCovered: number;
  totalControls: number;
  uncompletedControls: number;
}

export interface RiskProjectedScore {
  currentScore: number;
  projectedScore: number;
  updatedProjectedScore: number;
  targetScore: number;
}

export interface RiskBudget {
  budgetType: string;
  totalBudget: number;
  usedBudget: number;
  remainingBudget: number;
  quarters: QuarterlyBudget[];
}

export interface QuarterlyBudget {
  quarterLabel: string;
  allocated: number;
  used: number;
  remaining: number;
}

export enum RiskStatus {
  OPEN_RISK = 1,
  WAITING_FOR_REMOVAL = 2,
  MITIGATION = 3,
  ACCEPTED_RISK = 4,
}

export enum RiskStatusName {
  OPEN_RISK = 'Open Risk',
  MITIGATION = 'Mitigation',
  WAITING_FOR_REMOVAL = 'Waiting for Removal',
  ACCEPTED_RISK = 'Accepted risk',
}

export enum RiskStatusTypeName {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  REVIEWED = 'Reviewed',
}

export enum RiskSubStatus {
  ON_HOLD = 1,
  ACTIVE = 2,
  ACTIVE_CHANGED = 3,
}

export interface AddRiskDTO {
  planId: string;
  riskInOrgId?: string;
  riskTemplateId: string;
  instigators: string;
  likelihoodLevelId: number;
  impactLevelId: number;
  financialImpact: number;
  priority: number;
  riskProbability: number;
  controlMappings: {
    controlTemplateId: string;
    assetTypeIds: string[];
  }[];
  actionMappings: {
    controlTemplateIds: string[];
    actionId: string;
    isUnified: boolean;
    assetTypeSolutions: {
      assetTypeId: string;
      selectedToolId?: string;
      selectedLicenseTypeId?: string;
      isToolManual?: boolean;
      isServiceManual?: boolean;
      selectedServiceId?: string;
    }[];
  }[];
}

export interface SavePlanSimulationChangeDTO {
  planId: string;
  riskInPlanId: string;
  statusChanged?: boolean;
  rimsChanged?: boolean;
  targetDateChanged?: boolean;
  newStatus?: RiskStatusName;
  newStatusComment?: string;
  selectedRimsId?: string;
  rimsComment?: string;
  newTargetDate?: string;
  highlightRims?: boolean;
}

export interface ActiveRiskDetails {
  riskId: string;
  riskTemplateId: string;
  riskName: string;
  riMSOptionsSummary?: {
    currentSeverity: SeverityEnum;
    minCost: number | null;
    maxCost: number | null;
    minRemediationDays: number | null;
    maxRemediationDays: number | null;
    minTargetSeverity: SeverityEnum;
    maxTargetSeverity: SeverityEnum;
    optionsCount: number;
    targetSeverities: SeverityEnum[];
  } | null;
  status: RiskStatusName;
  statusComment: string;
  statusType: RiskStatusTypeName;
  subStatus: RiskSubStatus;
  approvedControlsCount: number;
  totalControlsCount: number;
  hasUnapprovedControls: boolean;
  allControlsCompleted: boolean;
  impactCost: number;
  isActiveRisk: boolean;
  riskScore: number;
  selectedRiMS: {
    riMSId: string;
    createdManually: boolean;
    currentSeverity: SeverityEnum;
    targetSeverity: SeverityEnum;
  };
  severity: SeverityEnum;
  updatesCount: number;
  riskUpdatesDaysAgo: number;
  complianceViolations: ActiveRiskComplianceCoverage;
  description?: string;
  affectedSystems: ActiveRiskAffectedAsset[];
  references: ActiveRiskReference[];
  changelog: ActiveRiskChangelogEntry[];
  comments: Comment[];
}

export interface ActiveRiskComplianceCoverage {
  contractual: {
    completed: number;
    total: number;
  };
  regulatory: {
    completed: number;
    total: number;
  };
  sensitiveData: {
    completed: number;
    total: number;
  };
}

export interface ActiveRiskAffectedAsset {
  assetTypeId: string;
  assetTypeDisplayName: string;
  assetCount: number;
}

export interface ActiveRiskReference {
  isBold: boolean;
  id: string;
  name: string;
  objectType: string;
  statusDisplayKey: string;
  statusIcon: StatusIconType;
}

export interface ActiveRiskChangelogEntry {
  eventTypeName: string;
  description: string;
  changeDate: string;
}
